<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-semibold mr-auto">
        {{ isEditing ? 'Edit Template' : 'New Template' }}
      </h1>
      <Button label="Back to List" icon="pi pi-arrow-left" severity="secondary" @click="goBack" />
    </div>

    <div class="grid md:grid-cols-2 gap-8">
      <Card>
        <template #title>
          <div class="space-y-4">
            <div>
              <label class="block font-medium">Template Name</label>
              <InputText v-model="form.name" class="w-full" :class="{ 'p-invalid': nameError }" />
              <small v-if="nameError" class="p-error">{{ nameError }}</small>
              <small v-else class="text-gray-500">
                Template name must be unique and contain only letters, numbers, and special
                characters: -_~%#@
              </small>
            </div>

            <div>
              <label class="block font-medium">Subject</label>
              <InputText v-model="form.subject" class="w-full" />
            </div>

            <div>
              <label class="block font-medium mb-1">Body</label>
              <RichTextEditor v-model="form.html" />
            </div>
          </div>
        </template>

        <template #footer>
          <div class="flex justify-end gap-2">
            <Button label="Cancel" severity="secondary" @click="goBack" />
            <Button
              label="Save"
              icon="pi pi-check"
              @click="save"
              :disabled="!form.name || !form.subject || !!nameError"
            />
          </div>
        </template>
      </Card>

      <Card>
        <template #title>Preview</template>
        <div class="prose max-w-none" v-html="renderedPreview" />
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useEmailTemplateStore, type EmailTemplate } from '@/stores/emailTemplateStore'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Card from 'primevue/card'
import { useToast } from 'primevue/usetoast'

const route = useRoute()
const router = useRouter()
const store = useEmailTemplateStore()
const toast = useToast()

const id = route.params.id as string | undefined
const isEditing = computed(() => !!id)

const form = reactive({
  name: '',
  subject: '',
  html: '',
  createdAt: undefined as Date | undefined,
  updatedAt: undefined as Date | undefined,
})

const nameError = ref<string | null>(null)

// Validate template name to ensure it conforms to Firestore ID requirements
const isValidTemplateName = computed(() => {
  // Firestore IDs must be non-empty strings, no more than 1500 bytes in size,
  // and contain only the following characters: a-z, A-Z, 0-9, and the following special characters: -_~%#@
  const validNameRegex = /^[a-zA-Z0-9\-_~%#@]+$/
  return form.name.length > 0 && form.name.length <= 1500 && validNameRegex.test(form.name)
})

// Check if template name is unique
const checkNameUniqueness = async () => {
  if (!form.name || form.name === id) return true

  return !store.items.some((t) => t.name === form.name && t.id !== id)
}

watch(
  () => form.name,
  async () => {
    if (!isValidTemplateName.value) {
      nameError.value =
        'Template name must contain only letters, numbers, and the following special characters: -_~%#@'
    } else if (!(await checkNameUniqueness())) {
      nameError.value = 'Template name must be unique'
    } else {
      nameError.value = null
    }
  },
)

onMounted(async () => {
  if (id) {
    await store.load()
    const template = store.items.find((t) => t.id === id)
    if (template) {
      form.name = template.name
      form.subject = template.subject
      form.html = template.html
      form.createdAt = template.createdAt
      form.updatedAt = template.updatedAt
    }
  }
})

function goBack() {
  router.push('/crm/email-templates')
}

async function save() {
  if (nameError.value) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: nameError.value,
      life: 3000,
    })
    return
  }

  const payload: Partial<EmailTemplate> = {
    name: form.name,
    subject: form.subject,
    html: form.html,
  }

  try {
    if (id) {
      // Update existing template
      await store.update(id, payload)
    } else {
      // Add new template with name as the ID
      await store.add({
        ...payload,
        id: form.name,
      })
    }

    toast.add({
      severity: 'success',
      summary: 'Template Saved',
      detail: `Template "${form.name}" has been saved successfully.`,
      life: 3000,
    })

    goBack()
  } catch (error) {
    console.error('Failed to save template:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save template. Please try again.',
      life: 3000,
    })
  }
}

function interpolate(template: string, data: Record<string, unknown>): string {
  return template.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
    return key.split('.').reduce((obj: Record<string, unknown>, k: string) => obj?.[k], data) ?? ''
  })
}

const sampleData = {
  firstName: 'Cody',
  lastName: 'Sparks',
  organization: {
    name: 'Scrap Bros',
  },
  transaction: {
    total: '$5,200.00',
    date: 'April 10, 2025',
  },
}

const renderedPreview = computed(() => interpolate(form.html, sampleData))
</script>

<style lang="scss">
/* Basic editor styles */
.tiptap {
  :first-child {
    margin-top: 0;
  }

  /* List styles */
  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    margin-top: 2.5rem;
    text-wrap: pretty;
  }

  h1,
  h2 {
    margin-top: 3.5rem;
    margin-bottom: 1.5rem;
  }

  h1 {
    font-size: 1.4rem;
  }

  h2 {
    font-size: 1.2rem;
  }

  h3 {
    font-size: 1.1rem;
  }

  h4,
  h5,
  h6 {
    font-size: 1rem;
  }

  /* Code and preformatted text styles */
  code {
    background-color: var(--purple-light);
    border-radius: 0.4rem;
    color: var(--black);
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  pre {
    background: var(--black);
    border-radius: 0.5rem;
    color: var(--white);
    font-family: 'JetBrainsMono', monospace;
    margin: 1.5rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid var(--gray-3);
    margin: 1.5rem 0;
    padding-left: 1rem;
  }

  hr {
    border: none;
    border-top: 1px solid var(--gray-2);
    margin: 2rem 0;
  }

  /* Table-specific styling */
  table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;

    td,
    th {
      border: 1px solid var(--gray-3);
      box-sizing: border-box;
      min-width: 1em;
      padding: 6px 8px;
      position: relative;
      vertical-align: top;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      background-color: var(--gray-1);
      font-weight: bold;
      text-align: left;
    }

    .selectedCell:after {
      background: var(--gray-2);
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      background-color: var(--purple);
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
    }
  }

  .tableWrapper {
    margin: 1.5rem 0;
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }
}
</style>
